import React, { useEffect, useState, useContext } from 'react';
import * as TreeTypeRepository from '../../../Repository/TreeTypeRepository';
import * as ContractRepository from '../../../Repository/ContractRepository';
import * as UserRepository from '../../../Repository/UserRepository';

import { useParams } from 'react-router';
import { entriesToFormData, getUserName } from '../../../Helper/DataHelper';
import { SelectValue } from "../../../Component/MultipleRemoteSelector";
import { Col, Form, Input, Radio, Result, Row, Select, Skeleton, Tabs } from 'antd';
import { Contract, TreeType, User } from 'tree-wallet-client';
import { Typography } from 'antd';
import { MultipleChoicesWithTable, MultipleSelectWithTableValue } from "../../../Component/MultipleSelectWithTable/MultipleChoicesWithTable";

import MultipleChoicesTable from '../../../Component/MultipleSelectWithTable/MultipleChoicesTable';
import MultipleChoicesTableUsers from "../../../Component/MultipleSelectWithTable/MultipleChoicesTableUsers/MultipleChoicesTableUsers";

import Joi from "joi";
import Button from "antd/es/button";
import message from "antd/es/message";
import Page from '../../../Component/Page/Page';
import find from 'lodash/find';
import { GlobalContext } from "../../../Contexts/GlobalContext";
import { CONTRACT_SAVE_FAILED, CONTRACT_UPDATED } from '../../../Helper/BannerMessages';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const allowedRoles = [UserRepository.UserRoles.Admin];

export const contractValidationSchema = Joi.object({
  language: Joi.string(),
  description: Joi.string(),
  treeTypes: Joi.string(),
  users: Joi.string(),
  advanced: Joi.boolean(),
});

interface TreeTypeDTO {
  value: number;
  label: string;
}

interface ContractData {
  trees: number;
  users: User[];
  contract: Contract;
}

export interface EditedUser {
  id?: any;
  contractRoles?: string[];
}

export default function AdminContractEditionPage () {
  const [treeTypes, setTreeTypes] = useState<TreeTypeDTO[]|undefined>(undefined);
  const [contractData, setContractData] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const setLandOwners = useState<any[]|undefined>(undefined)[1];
  const [contractUsers, setContractUsers] = useState<MultipleSelectWithTableValue[]|undefined>(undefined);
  const { updateState } = useContext(GlobalContext);
  const { contractId } = useParams<"contractId">() ;
  const [form] = Form.useForm();

  async function fetchUserList(queryString: string, contractId?: number|string): Promise<SelectValue[]> {
    return UserRepository.getAdminUsersList({
      params: {
        contractId,
        filters: JSON.stringify([
          { field: "email", value: queryString },
          { field: "search", value: queryString }
        ])
      }
    })
      .then((body) => {
        return body.map((user: User) => {
          return {
            label: getUserName(user),
            value: user,
            id: user.id.toString()
          }
        })})
      .catch((error) => {
        // The search field keeps what it shows: a search that failed has no result to offer
        console.error('could not search the users of the contract', { error });
        return [];
      });
  }

  const onFinish = async (values: any) => {
    const submittedData = {
      description: values.description,
      language: values.language,
      advanced: values.advanced,
      treeTypes: JSON.stringify(values.treeTypes),
      users: JSON.stringify(values.users.map((user: User) => {
        return {
          id: user.id,
          roles: user.roles
        }
      })),
    };

    if (submittedData.treeTypes === undefined) {
      submittedData.treeTypes = JSON.stringify(contractData.contract.treeList)
    }

    try {
      setIsSaving(true);
      await contractValidationSchema.validateAsync(submittedData).catch(err => console.error(err, submittedData));
      const formData = entriesToFormData(Object.entries(submittedData));
      await ContractRepository.updateContract(formData, {
        params: { contractId },
        successCallback: async () => {
          message.success(CONTRACT_UPDATED);

          try {
            const contracts = await ContractRepository.getContractsList();

            // No token, no list: writing that empty one would empty the contracts the app runs on
            if (!contracts.length) {
              return;
            }

            // The Tree Form reads the contract from the global state, not from that list: without this
            // the saved preferences (`advanced`, hence the Number row) only showed up after a reload
            const savedContract = contracts.find((item: Contract) => String(item.id) === String(contractId));
            updateState && updateState({ contracts, ...(savedContract ? { contract: savedContract } : {}) })
          } catch (error) {
            console.error('could not reload the contracts after the save', { error });
          }
        },
        errorCallback: (error: any) => {
          message.error(error?.message || CONTRACT_SAVE_FAILED);
        }
      });
    } catch (err) {
      console.error(err, values, submittedData);
    } finally {
      setIsSaving(false);
    }
  };

  const componentDidMount = async () => {
    try {
      const treeTypes = await TreeTypeRepository.getTreeTypesList({});
      treeTypes && setTreeTypes(treeTypes.map((treeType: TreeType) => {
        return {
          label: treeType.translations.latin,
          value: treeType.id,
          id: treeType.id,
        }
      }));
    } catch (err) {
      console.error(err);
    }

    const contractData: ContractData | undefined = await ContractRepository.getAdminContract(contractId, { params: { id: contractId } })
      .catch((err) => {
        // Without this the skeleton stays up: the page says the contract could not be read
        console.error('could not read the contract', err);
        message.error('The contract could not be read');
        return undefined;
      });

    if (!contractData) {
      setIsLoading(false);
      return;
    }

    const contractUsers: any[] = [];
    const landOwners: any[] = [];
    contractData.users && contractData.users.map((user: any) => {
      const getUser = (user: User) => {
        return {
          id: user.id,
          value: user,
          label: getUserName(user)
        }
      }
      if (!ContractRepository.hasRole(user.roles, ContractRepository.roles.LandOwner.name)) {
        contractUsers.push(getUser(user));
      } else {
        landOwners.push(getUser(user));
      }
    });

    setLandOwners(landOwners)
    setContractUsers(contractUsers)
    setContractData(contractData)

    await form.setFieldsValue({
      'treeTypes': contractData.contract.treeList,
      'users': contractUsers && contractUsers.map(user => {
        return { id: user.id, roles: user.value.contractRoles };
      }),
    });

    await form.setFieldsValue({ 'treeTypes': contractData.contract.treeList });

    setIsLoading(false);
  }

  const onEdition = (editedUser: EditedUser|undefined, selectedValues: MultipleSelectWithTableValue[], setSelectedValues: Function) => {
    if (!editedUser) {
      return;
    }

    const updatedSelectedValues = selectedValues?.map((item) => {
      if (parseInt(String(editedUser.id)) === parseInt(String(item.id))) {
        return { ...item, value: { ...item.value, contractRoles: editedUser.contractRoles } };
      }
      return item;
    });

    setSelectedValues(updatedSelectedValues);
  };

  const onFinishFailed = (err: any) => {
    message.error(err);
  };

  useEffect(() => {
    componentDidMount();
  }, [])

  if (!contractId) {
    return <Page allowedRoles={allowedRoles} title={`TreeWallet`}>
      <Result status="error" title={"No contract defined"} />
    </Page>;
  }

  if (isLoading) {
    return <Page allowedRoles={allowedRoles} title={`TreeWallet`}>
      {/* Same heading as the loaded page: without it the skeleton started one line too high */}
      <Row><Col><Title level={3}>Edit Contract</Title></Col></Row>
      <Skeleton active/>
    </Page>
  }

  if (!isLoading && (!contractData || !contractData.users)) {
    return <Page allowedRoles={allowedRoles} title={`TreeWallet`}>
      <Result status="error" title={"There was an unexpected error"} />
    </Page>
  }

  // @ts-ignore
  return (
    <Page allowedRoles={allowedRoles} title={`TreeWallet`}>
      <Row><Col><Title level={3}>Edit Contract</Title></Col></Row>
      <div>
        <Form form={form} name="basic" onFinish={onFinish} onFinishFailed={onFinishFailed}>
          <div>
            <Row><Col><Title level={5}>General information</Title></Col></Row>
            <Row>
              <Col xs={8} sm={4}>ID</Col>
              <Col xs={{ offset: 2 }}>{contractData.contract.id}</Col>
            </Row>
            <Row>
              <Col xs={8} sm={4}>Nb Trees</Col>
              <Col xs={{ offset: 2 }}>{contractData?.trees}</Col>
            </Row>
            <Row>
              <Form.Item
                initialValue={contractData?.contract?.description}
                name="description"
                label="Description"
                rules={[{
                  required: true,
                  message: 'Description cannot be empty'
                }]}
              >
                <Input />
              </Form.Item>
            </Row>
            <Row>
              <Form.Item
                initialValue={contractData.contract.preferences?.language}
                name="language"
                label="Tree list language"
                rules={[{
                  required: true,
                  message: 'You must choose a language'
                }]}
              >
                <Select value={contractData.contract.preferences?.language}>
                  {TreeTypeRepository.Translations.map((translation: string) => {
                    return <Option key={'translation_' + translation} value={translation}>{translation}</Option>
                  })}
                </Select>
              </Form.Item>
            </Row>
            <Row>
              <Form.Item
                initialValue={!!contractData.contract.preferences?.advanced}
                name="advanced"
                label="Advanced view"
                rules={[{
                  required: true,
                  message: 'You must choose a form type'
                }]}
              >
                <Radio.Group defaultValue={!!contractData.contract.preferences?.advanced}>
                  <Radio value={false}>Normal</Radio>
                  <Radio value={true}>Advanced</Radio>
                </Radio.Group>
              </Form.Item>
            </Row>
            <Row>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={isSaving}>
                  Save
                </Button>
              </Form.Item>
            </Row>
          </div>
          <Tabs defaultActiveKey="1">
            <TabPane tab={`Users`} key="1">
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="users"
                    rules={[{
                      required: false,
                      message: 'You must select at least one user'
                    }]}
                  >
                    <MultipleChoicesWithTable
                      name={'users'}
                      template={MultipleChoicesTableUsers}
                      onSearch={fetchUserList}
                      placeholder={'User search'}
                      onEdition={onEdition}
                      initialValues={contractUsers}
                      transformers={[(value: any) => {
                        return { id: value.id, roles: value.value.contractRoles }
                      }]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab={`Trees Types`} key="2">
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="treeTypes"
                    rules={[{
                      required: false,
                      message: 'You must select at least one tree type'
                    }]}
                  >
                    <MultipleChoicesWithTable
                      name={'treeTypes'}
                      template={MultipleChoicesTable}
                      selectDataSource={treeTypes}
                      transformers={[(data: MultipleSelectWithTableValue) => data.value.toString()]}
                      initialValues={contractData?.contract?.treeList?.map((treeTypeId: number) => {
                        return {
                          label: treeTypes && find(treeTypes, ['value', treeTypeId])?.label,
                          value: treeTypeId.toString(),
                          id: treeTypeId.toString(),
                        }
                      })}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        </Form>
      </div>
    </Page>
  );
}
